import React, { useState, useEffect } from "react";
import { ErrorResponse } from "../../../Models/Error.js";
import { Shipment } from "../../../Models/Shipment.js";
import { useAppContext } from "../../../AppProvider.jsx";
import {
  callSuccessToast,
  callErrorToast,
} from "../../../utilities/utilities.ts";
import { ShipmentsService } from "../../../services/shipment/shipments.ts";
import { useNavigate } from "react-router-dom";
import {
  ShipmentConfirmContextValue,
  ShipmentsConfirmProviderProps,
} from "./types.ts";
import { ShipmentConfirmContext } from "./contexts.ts";

const shipmentsService = new ShipmentsService();

/* ===========================================================================
 * PROVIDER: ShipmentConfirmProvider
 * =========================================================================== */

/**
 * Handles confirming one or more shipments.
 * - Executes multiple confirmation requests concurrently.
 * - Tracks loader and error state.
 * - Exposes `confirmShipments` function via context.
 */
export const ShipmentConfirmProvider: React.FC<
  ShipmentsConfirmProviderProps
> = ({ children, data = [], callback }) => {
  const navigate = useNavigate(); // React Router navigation
  const { dictionary } = useAppContext(); // App-wide dictionary for translations/messages

  // State to store API errors during confirmation
  const [error, setError] = useState<ErrorResponse<Shipment> | null>(null);

  // Loader state to indicate ongoing confirmation operations
  const [loader, setLoader] = useState<boolean>(false);

  /**
   * Confirm multiple shipments concurrently.
   * Handles partial failures gracefully.
   */
  const confirmShipments = async (): Promise<void> => {
    setLoader(true);

    try {
      const results = await Promise.allSettled(
        data.map((id) => shipmentsService.confirm(id)),
      );

      // Separate successful and failed confirmations
      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      const success = results.filter(
        (r): r is PromiseFulfilledResult<void> => r.status === "fulfilled",
      );

      // Handle failures
      if (failed.length > 0) {
        const firstError = failed[0].reason as ErrorResponse<Shipment>;
        setError(firstError);

        // If some confirmations succeeded, call callback to refresh UI/state
        if (success.length > 0 && callback) {
          callback();
        }

        return;
      }

      // All confirmations succeeded
      if (callback) callback();
      callSuccessToast(dictionary.messages.save_successful); // Notify success
      navigate("/shipments"); // Navigate to shipments list
    } catch (unexpected) {
      // Fallback for unexpected errors
      const err: ErrorResponse<Shipment> = {
        response: { data: {} },
      };
      setError(err);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECT: Show error toast if an error occurs during confirmation
   * ------------------------------------------------------------------------- */
  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  /* =========================================================================
   * CONTEXT VALUE
   * ======================================================================= */
  const contextValue: ShipmentConfirmContextValue = {
    data,
    loader,
    confirmShipments,
    callback,
  };

  return (
    <ShipmentConfirmContext.Provider value={contextValue}>
      {children}
    </ShipmentConfirmContext.Provider>
  );
};
